<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div
            class="tw-mx-auto tw-w-[95%] md:tw-max-w-[700px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-md tw-my-5 tw-rounded">
            <div class="tw-p-4 tw-text-lg tw-border-b tw-border-solid tw-font-medium tw-text-gray-600 tw-bg-gray-50">
                {{ isEdit ? $t('portal.purchases.edit') : $t('portal.purchases.assign') }}
            </div>

            <div class="tw-p-5 tw-bg-white">
                <div class="tw-grid tw-grid-cols-12 tw-gap-x-4">
                    <div v-if="isEdit" class="tw-col-span-12">
                        <FormField v-model="form.code" :label="$t('portal.fields.code')" type="text" :error="err('code')" :help="$t('portal.fields.code-help')" />
                    </div>
                    <div class="tw-col-span-12">
                        <FormField v-model="form.product_id" :label="$t('fields.product')" type="select"
                            :options="productOptions" :error="err('product_id')" @update:modelValue="onProductChange" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField v-model="form.price" :label="$t('fields.price')" type="number" :error="err('price')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField v-model="form.cost" :label="$t('fields.buying-price')" type="number" :error="err('cost')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField v-model="form.start_date" :label="$t('portal.fields.start-date')" type="date" :error="err('start_date')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField v-model="form.expiry_date" :label="$t('portal.fields.expiry-date-optional')" type="date" :error="err('expiry_date')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6" v-if="isEdit">
                        <FormField v-model="form.status" :label="$t('fields.status')" type="select" :options="statusOptions" :error="err('status')" />
                    </div>
                    <div class="tw-col-span-12">
                        <FormField v-model="form.note" :label="$t('fields.customer_notes')" type="textarea" :error="err('note')" />
                    </div>
                </div>
            </div>

            <div class="tw-p-4 tw-border-t tw-justify-end tw-flex tw-items-center tw-gap-2 tw-bg-gray-50">
                <a-button :disabled="loading" @click="onCancel" intent="simple" class="tw-min-w-[120px]">{{ $t('buttons.cancel') }}</a-button>
                <a-button :disabled="loading" :loading="loading" @click="onSave" class="tw-min-w-[120px]">{{ isEdit ? $t('buttons.update') : $t('buttons.add') }}</a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField';
import { all as allProducts } from '@/api/products';
import { create as createPurchase, update as updatePurchase } from '@/api/purchases';
import useAlert from '@/composables/useAlert';
import { useValidation } from '@/composables/useValidation';

const props = defineProps({
    visible: { type: Boolean, default: false },
    customerId: { type: [Number, String], default: null },
    purchase: { type: Object, default: null },
});
const emit = defineEmits(['cancel', 'saved']);
const i18n = useI18n();

const loading = ref(false);
const errors = ref({});
const products = ref([]);
const form = ref(defaults());
const err = useValidation(errors);
const isEdit = computed(() => !!props.purchase?.id);

const productOptions = computed(() => products.value.map((p) => ({ value: p.id, label: `${p.name} (${p.type})` })));
const statusOptions = [
    { value: 'active', label: i18n.t('common.active') },
    { value: 'expired', label: i18n.t('portal.status.expired') },
    { value: 'cancelled', label: i18n.t('portal.status.cancelled') },
];

function defaults() {
    return { product_id: '', price: '', cost: '', start_date: new Date().toISOString().slice(0, 10), expiry_date: '', status: 'active', note: '' };
}

async function loadProducts() {
    try {
        const { data } = await allProducts({ status: 'active' });
        products.value = data.products || [];
    } catch (e) { /* interceptor */ }
}

const onProductChange = (id) => {
    const p = products.value.find((x) => x.id == id);
    if (p && !isEdit.value) {
        form.value.price = p.selling_price;
        form.value.cost = p.cost;
    }
};

watch(() => props.visible, (v) => {
    if (v) {
        errors.value = {};
        form.value = props.purchase ? { ...defaults(), ...props.purchase } : defaults();
        if (!products.value.length) loadProducts();
    }
});

const onCancel = () => { if (!loading.value) emit('cancel'); };

const onSave = async () => {
    loading.value = true;
    errors.value = {};
    try {
        const payload = { ...form.value, customer_id: props.customerId };
        if (!payload.expiry_date) delete payload.expiry_date;
        const req = isEdit.value ? updatePurchase(props.purchase.id, payload) : createPurchase(payload);
        const { data } = await req;
        if (data.code === 'SUCCESS') {
            useAlert(i18n.t(isEdit.value ? 'messages.updated-successfully' : 'messages.created-successfully', { field: i18n.t('portal.entities.purchase') }));
            emit('saved', data.purchase);
        }
    } catch (e) {
        if (e.response?.status === 422) {
            for (const k in e.response.data.errors) errors.value[k] = e.response.data.errors[k][0];
        }
    } finally {
        loading.value = false;
    }
};
</script>
